import React, { useCallback, useEffect, useState } from 'react'
import { ReceiptIcon, Loader2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  fetchMyExpenses,
  submitExpense,
  type JobExpense,
  type ExpenseStatus,
} from '../api/expenses'
import { usePermissions } from '../hooks/usePermissions'
import {
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileEmptyState,
  MobileSection,
  mobile,
} from '../components/mobile'

function expenseStatusClass(status: ExpenseStatus): string {
  switch (status) {
    case 'approved':
      return 'bg-green-100 text-green-800'
    case 'rejected':
      return 'bg-red-100 text-red-800'
    case 'reimbursed':
      return 'bg-blue-100 text-blue-800'
    default:
      return 'bg-yellow-100 text-yellow-800'
  }
}

function formatStatus(status: ExpenseStatus, t: (key: string) => string): string {
  return t(`expenses.status.${status}`)
}

export function Expenses() {
  const { t } = useTranslation('field')
  const { can, loading: permLoading } = usePermissions()
  const canSubmit = can('expenses.submit')
  const [items, setItems] = useState<JobExpense[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [description, setDescription] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [paidOutOfPocket, setPaidOutOfPocket] = useState(false)
  const [amount, setAmount] = useState('')
  const [jobId, setJobId] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setItems(await fetchMyExpenses())
    } catch (e) {
      setError(e instanceof Error ? e.message : t('expenses.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    if (permLoading || !canSubmit) return
    load()
  }, [permLoading, canSubmit, load])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!description.trim()) return
    const qty = parseFloat(quantity)
    if (Number.isNaN(qty) || qty <= 0) {
      setError(t('expenses.qtyInvalid'))
      return
    }
    let parsedAmount: number | null = null
    if (paidOutOfPocket) {
      parsedAmount = parseFloat(amount)
      if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
        setError(t('expenses.amountInvalid'))
        return
      }
    }
    setSubmitting(true)
    setError(null)
    try {
      await submitExpense({
        description: description.trim(),
        quantity: qty,
        paid_out_of_pocket: paidOutOfPocket,
        amount: parsedAmount,
        job_id: jobId.trim() || null,
      })
      setDescription('')
      setQuantity('1')
      setPaidOutOfPocket(false)
      setAmount('')
      setJobId('')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('expenses.submitFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  if (permLoading) {
    return (
      <MobilePage className="flex justify-center items-center min-h-[400px]">
        <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
      </MobilePage>
    )
  }

  if (!canSubmit) {
    return (
      <MobilePage>
        <p className="text-gray-600">{t('expenses.noPermission')}</p>
      </MobilePage>
    )
  }

  return (
    <MobilePage>
      <MobilePageHeader
        title={t('expenses.title')}
        subtitle={t('expenses.subtitle')}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>
      )}

      <MobileSection title={t('expenses.newExpense')}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className={mobile.label}>{t('expenses.description')}</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                className={mobile.field}
              />
            </div>
            <div>
              <label className={mobile.label}>{t('expenses.quantity')}</label>
              <input
                type="number"
                min="0.01"
                step="any"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className={mobile.field}
              />
            </div>
            <div>
              <label className={mobile.label}>{t('expenses.jobIdOptional')}</label>
              <input
                type="text"
                value={jobId}
                onChange={(e) => setJobId(e.target.value)}
                placeholder="UUID"
                className={mobile.field}
              />
            </div>
          </div>
          <label className="flex items-center gap-2 min-h-[44px] text-sm text-gray-700">
            <input
              type="checkbox"
              checked={paidOutOfPocket}
              onChange={(e) => setPaidOutOfPocket(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            {t('expenses.paidOutOfPocket')}
          </label>
          {paidOutOfPocket && (
            <div>
              <label className={mobile.label}>{t('expenses.amountDollars')}</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                className={`${mobile.field} max-w-xs`}
              />
            </div>
          )}
          <button type="submit" disabled={submitting} className={`${mobile.primaryBtn} w-full sm:w-auto`}>
            {submitting ? '…' : t('expenses.submit')}
          </button>
        </form>
      </MobileSection>

      <p className={mobile.sectionLabel}>{t('expenses.myExpenses')}</p>
      <div className="native-mobile-list space-y-2">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : items.length === 0 ? (
          <MobileEmptyState
            icon={<ReceiptIcon className="w-12 h-12" />}
            title={t('expenses.emptySubmitted')}
          />
        ) : (
          items.map((row) => (
            <MobileListRow
              key={row.id}
              title={row.description}
              badges={
                <span className={`${mobile.badge} ${expenseStatusClass(row.status)}`}>
                  {formatStatus(row.status, t)}
                </span>
              }
              meta={
                <div>
                  {t('expenses.qtyShort', { qty: row.quantity ?? '—' })}
                  {row.paid_out_of_pocket && row.amount != null ? ` · $${row.amount.toFixed(2)}` : ''}
                  {' · '}
                  {new Date(row.created_at).toLocaleDateString()}
                </div>
              }
              chevron={false}
            />
          ))
        )}
      </div>

      <div className="native-desktop-table bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{t('expenses.myExpenses')}</h2>
        </div>
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-500 px-6 py-8">No expenses submitted yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {items.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{row.description}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{row.quantity ?? '—'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {row.paid_out_of_pocket && row.amount != null ? `$${row.amount.toFixed(2)}` : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${expenseStatusClass(row.status)}`}
                      >
                        {formatStatus(row.status, t)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(row.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </MobilePage>
  )
}
